import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';
import { useState } from 'react';
import { toast } from 'sonner';
import { useTimemanStore } from '@/store/timeman';
import { useElapsedTimeActions } from './elapsed-times/useElapsedTimeActions';
import { useCompleteTask, useTaskActions } from './useTaskActions';
import { addComment, createTask } from '@/entities/task/api/tasks';
import {
  completeIntermediate,
  createIntermediate,
  intermediateErrorMessage,
  useIntermediateStore,
} from '@/entities/intermediate';

interface UseIntermediateTaskProps {
  defaultDuration?: number;
  type?: 'intermediate' | 'meeting';
}

/**
 * Промежуточные дела живут в отдельной серверной системе трекинга
 * (entities/intermediate: свои pause/resume/complete + WS) — общий таймер
 * задач не трогаем. Встречи остаются на старом механизме (общий таймер,
 * один активный на пользователя).
 */
export const useIntermediateTask = (props: UseIntermediateTaskProps = {}) => {
  const { defaultDuration = 20, type = 'intermediate' } = props;

  const { updateTask } = useTaskActions();
  const { startTask, stopTask } = useTaskTimerStore();
  const { createElapsedTime } = useElapsedTimeActions();
  const { completeTask } = useCompleteTask();

  const [isLoading, setIsLoading] = useState(false);

  const taskTypeConfig = {
    intermediate: {
      title: 'Промежуточные дела',
      defaultDuration: 20,
      descriptionPrefix: 'Промежуточные дела',
      color: 'blue',
      tags: ['промежуточные'],
    },
    meeting: {
      title: 'Встреча',
      defaultDuration: 60,
      descriptionPrefix: 'Встреча',
      color: 'teal',
      tags: ['встречи'],
    },
  };

  const MEETING_GROUP_ID = 6;

  const config = taskTypeConfig[type];

  const createIntermediateTask = async (
    comment: string,
    durationMinutes: number = defaultDuration,
    userId: number
  ) => {
    if (!userId) {
      toast.error('Не определен ID пользователя');
      return null;
    }

    setIsLoading(true);

    try {
      const trimmed = comment.trim();
      const taskTitle = trimmed
        ? `${config.title}: ${trimmed.substring(0, 50)}${
            trimmed.length > 50 ? '...' : ''
          }`
        : config.title;

      if (type === 'meeting') {
        // Встреча: обычная задача + общий таймер. POST /tasks/intermediate не
        // используем — он теперь регистрирует серверный трекинг «дел», и
        // встреча попала бы в список промежуточных дел.
        const created = await createTask({
          TITLE: taskTitle,
          RESPONSIBLE_ID: userId,
          TIME_ESTIMATE: durationMinutes * 60,
          GROUP_ID: MEETING_GROUP_ID,
          ...(trimmed ? { DESCRIPTION: trimmed } : {}),
        });
        const createdTaskId = Number(
          (created as ANY)?.id ?? (created as ANY)?.taskId
        );
        if (!createdTaskId) {
          toast.error('Не удалось создать задачу: сервер не вернул ID');
          return null;
        }
        const taskId = String(createdTaskId);
        await startTask(taskId, {
          title: taskTitle,
          plannedMinutes: durationMinutes,
          kind: type,
        });
        toast.success(`${config.title} создана и запущена`);
        return taskId;
      }

      // Ответ — сразу полное состояние трекинга, дело уже запущено на бэке.
      const state = await createIntermediate({
        description: trimmed || undefined,
        durationMinutes,
      });

      const createdTaskId = Number(state?.taskId);
      if (!createdTaskId) {
        toast.error('Не удалось создать задачу: сервер не вернул ID');
        return null;
      }

      const store = useIntermediateStore.getState();
      store.upsert(state);
      if (trimmed) {
        store.setDescription(createdTaskId, trimmed);
      }
      // Как и раньше: старт дела открывает рабочий день, если он закрыт.
      const timemanStore = useTimemanStore.getState();
      if (timemanStore.status === 'closed') {
        timemanStore.openWorkday().catch((error) => {
          console.error(
            'Не удалось автоматически открыть рабочий день:',
            error
          );
        });
      }

      toast.success(`${config.title} создана и запущена`);
      return String(createdTaskId);
    } catch (error: ANY) {
      toast.error(
        type === 'intermediate'
          ? intermediateErrorMessage(error)
          : 'Не удалось создать встречу'
      );
      console.error('Ошибка при создании промежуточного дела:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const updateIntermediateTask = async (
    taskId: string,
    comment: string,
    durationMinutes: number = defaultDuration
  ) => {
    if (!comment.trim()) {
      toast.error('Пожалуйста, добавьте комментарий');
      return;
    }

    try {
      await updateTask({
        id: taskId,
        payload: {
          TITLE: config.title,
          DESCRIPTION: `${comment}\n\n---\nТип: ${config.descriptionPrefix}\nПланируемое время: ${durationMinutes} минут`,
          // План промежуточного дела правится через PATCH /time (±минуты),
          // TIME_ESTIMATE трогаем только у встреч (старый механизм).
          ...(type === 'meeting'
            ? { TIME_ESTIMATE: durationMinutes * 60 }
            : {}),
        },
      });

      if (type === 'intermediate') {
        useIntermediateStore
          .getState()
          .setDescription(Number(taskId), comment.trim());
      }

      toast.success('Комментарий задачи обновлен');
      return true;
    } catch (error) {
      console.error('Ошибка при обновлении комментария задачи:', error);
      toast.error('Не удалось обновить комментарий');
      return false;
    }
  };

  const addIntermediateComment = async (taskId: string, text: string) => {
    if (!text.trim()) {
      return;
    }

    await addComment(taskId, `Промежуточные дела: ${text}`);
  };

  const stopIntermediateTask = async (taskId: string) => {
    if (!taskId) {
      return;
    }

    // Промежуточное дело: завершение целиком на бэке (флаш времени + закрытие
    // в Bitrix с перечиткой статуса). При сбое — дело остаётся paused, время
    // не теряется; показываем ошибку, а не тихий успех.
    if (type === 'intermediate') {
      try {
        const state = await completeIntermediate(Number(taskId));
        useIntermediateStore.getState().upsert(state);
        return true;
      } catch (error) {
        console.error('Ошибка при завершении промежуточного дела:', error);
        toast.error(intermediateErrorMessage(error));
        return false;
      }
    }

    try {
      const elapsedSeconds = stopTask(taskId, true);

      if (elapsedSeconds > 0) {
        await createElapsedTime({
          taskId: Number(taskId),
          seconds: elapsedSeconds,
        });
      }

      await completeTask({
        taskId,
        silent: true,
      });

      return true;
    } catch (error) {
      console.error('Ошибка при сохранении времени задачи:', error);
      toast.error(`Не удалось сохранить ${config.title.toLowerCase()}`);
      return false;
    }
  };

  return {
    createIntermediateTask,
    updateIntermediateTask,
    stopIntermediateTask,
    addIntermediateComment,
    isLoading,
    taskType: type,
    defaultDuration: config.defaultDuration,
    title: config.title,
    color: config.color,
  };
};
